/* eslint-disable react/forbid-dom-props */
import { Currency, CURRENCY_CHAR_MAP, ProfitabilityEstimateDTO } from 'api';
import { Box, formatNumber, Tooltip } from 'ui';
import { HelpOutlineOutlined } from 'ui/icons';

type ResultTableProps = {
  data: ProfitabilityEstimateDTO;
};

const formatAmount = (value: string | undefined | null, currencyCode: Currency = Currency.GBP) => {
  if (!value) {
    return '-';
  }

  const formattedAmount = new Intl.NumberFormat().format(Number(value));

  if (!currencyCode) return formattedAmount;

  return `${CURRENCY_CHAR_MAP[currencyCode]}${formattedAmount}`;
};

const formatPercentage = (value: string | undefined | null) => {
  if (!value) {
    return '-';
  }
  return formatNumber(value, {
    minimumFractionDigits: 2,
    style: 'percent',
  });
};

export const ResultTable = (props: ResultTableProps) => {
  const { data } = props;

  const { revenues, costs, net_revenue, net_take_rate, deal_margin, deal_margin_percentage_of_gmv } = data;

  return (
    <div className="row">
      <div className="col-12 col-md-3 border-end">
        <h4>Revenues</h4>
        <p data-testid="material-revenue">Material: {formatAmount(revenues.material_revenue_gbp)}</p>
        <p data-testid="pern-revenue">
          Pern Revenue: {formatAmount(revenues.pern_revenue_gbp)}
          <Box component="span" pl={1}>
            <Tooltip title={'Only a percentage of material can be claimed'}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="gmv" className="text-primary">
          <strong>
            GMV: <span>{formatAmount(revenues.gmv)}</span>
          </strong>
        </p>
      </div>
      <div className="col-12 col-md-3 border-end">
        <h4>Costs</h4>
        <p data-testid="material-cost">Material: {formatAmount(costs.material_cost_gbp)}</p>
        <p data-testid="shipping-cost">Shipping: {formatAmount(costs.shipping_cost_gbp)}</p>
        <p data-testid="buyer-agent-commission">
          Buyer Agent Commission: {formatAmount(costs.buyer_agent_commission_gbp)}
        </p>
        <p data-testid="seller-broker-commission">
          Seller Agent Commission: {formatAmount(costs.seller_agent_commission_gbp)}
        </p>
        <p data-testid="fx-cost">
          FX Cost Estimate: {formatAmount(costs.fx_cost)}
          <Box component="span" pl={1}>
            <Tooltip title={'0.2% hedge is applied to costs in EUR and revenue in USD'}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="inspections-cost">Inspections Estimate: {formatAmount(costs.inspection_cost)}</p>
        <p data-testid="insurance-cost">Insurance: {formatAmount(costs.insurance_cost)}</p>
        <p data-testid="bank-fees">Bank Fees: {formatAmount(costs.bank_fees)}</p>
        <p data-testid="financing-cost">
          Financing Cost Estimate: {formatAmount(costs.financing_cost)}
          <Box component="span" pl={1}>
            <Tooltip title={'Based on the payment terms and shipping route'}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="claims-cost">Claims Estimate: {formatAmount(costs.claims)}</p>
        <p data-testid="logistics-cost">Logistic Costs Estimate: {formatAmount(costs.logistic_cost)}</p>
        <p data-testid="unplanned-logistics-cost">
          Unplanned Logistic Costs Estimate: {formatAmount(costs.unplanned_logistic_costs)}
        </p>
        <p data-testid="total-costs" className="text-primary">
          <strong>
            Total Costs: <span>{formatAmount(costs.total_costs_gbp || costs.planned_costs_gbp)}</span>
          </strong>
        </p>
      </div>
      <div className="col-12 col-md-3 border-end">
        <h4>NTR %</h4>
        <p data-testid="net-revenue">
          Net Revenue: {formatAmount(net_revenue)}
          <Box component="span" pl={1}>
            <Tooltip title={'Net Revenue = GMV - Material - Third Party Commission - Shipping - FX Costs'}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="net-revenue-take-rate" className="text-primary">
          <strong>Take Rate: {formatPercentage(net_take_rate)}</strong>
        </p>
      </div>
      <div className="col-12 col-md-3">
        <h4>Deal Margin</h4>
        <p data-testid="deal-margin">
          Deal Margin: {formatAmount(deal_margin, Currency.GBP)}
          <Box component="span" pl={1}>
            <Tooltip title={'Deal Margin = GMV - Total Costs'}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="deal-margin-percentage-of-gmv" className="text-primary">
          <strong>Deal Margin % of GMV: {formatPercentage(deal_margin_percentage_of_gmv)}</strong>
        </p>
      </div>
    </div>
  );
};
